import React, { useState } from 'react';

const FORMAT_DOT_COLORS = {
  blog: 'bg-blue-400',
  tweet: 'bg-cyan-400',
  yt_script: 'bg-red-400',
  ig: 'bg-pink-400',
  tweet_thread: 'bg-teal-400'
};

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const ContentCalendar = ({ content = [], onSelectDate, selectedDate }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  const todayStr = now.toISOString().slice(0, 10);

  // Build date -> items map
  const dateMap = {};
  content.forEach(item => {
    const ts = item.createdAt;
    if (!ts) return;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const key = d.toISOString().slice(0, 10);
    if (!dateMap[key]) dateMap[key] = [];
    dateMap[key].push(item);
  });

  // Calendar grid
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  // Empty leading cells
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const monthLabel = new Date(year, month).toLocaleString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-5">
      {/* Month nav */}
      <div className="flex justify-between items-center mb-4">
        <button onClick={prevMonth} className="text-gray-400 hover:text-gray-200 px-2 py-1 transition-colors">&larr;</button>
        <h3 className="text-sm font-semibold text-gray-100">{monthLabel}</h3>
        <button onClick={nextMonth} className="text-gray-400 hover:text-gray-200 px-2 py-1 transition-colors">&rarr;</button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS.map(d => (
          <div key={d} className="text-center text-xs text-gray-500 font-medium py-1">{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} />;

          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const items = dateMap[dateStr] || [];
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;

          return (
            <button
              key={dateStr}
              onClick={() => onSelectDate && onSelectDate(dateStr)}
              className={`relative min-h-[3rem] p-1 rounded-lg text-left transition-colors ${
                isSelected
                  ? 'bg-blue-600/20 border border-blue-500'
                  : isToday
                    ? 'bg-gray-700/50 border border-yellow-500/50'
                    : 'bg-gray-900 border border-gray-700/50 hover:border-gray-600'
              }`}
            >
              <span className={`text-xs font-medium ${isToday ? 'text-yellow-400' : 'text-gray-400'}`}>
                {day}
              </span>
              {items.length > 0 && (
                <div className="flex flex-wrap gap-0.5 mt-1">
                  {items.slice(0, 4).map((item, j) => (
                    <span
                      key={j}
                      className={`w-2 h-2 rounded-full ${FORMAT_DOT_COLORS[item.format] || 'bg-gray-500'}`}
                      title={`${item.format}: ${item.title || item.topic || '(untitled)'}`}
                    />
                  ))}
                  {items.length > 4 && (
                    <span className="text-[9px] text-gray-500">+{items.length - 4}</span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex gap-3 mt-3 flex-wrap">
        {Object.entries(FORMAT_DOT_COLORS).map(([fmt, color]) => (
          <div key={fmt} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${color}`} />
            <span className="text-xs text-gray-500">{fmt}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContentCalendar;
